import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  COMPANY_SETTINGS_CATALOGUE,
  DUE_DATE_REMINDER_OPTIONS,
  DUE_DATE_REMINDER_SETTING,
  LOAN_RELEASE_SETTING,
  companySettingsOf,
  type CompanySettings,
} from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { describeError } from '@/lib/errors';
import { Card, ErrorState, PageHeader, Spinner } from '@/components/ui';

/**
 * Features each company switches on or off for itself. It acts on the company
 * picked in the header, so a tenant with several companies sets each one
 * separately. Saved the moment a switch is flipped; every change is audited.
 */
export function SystemSettingsPage() {
  const { companyId, company: money, can } = useAuth();
  const queryClient = useQueryClient();
  const canEdit = can('company:update');
  // Whoever runs the loans also sets how early their EMIs are released.
  const canEditLoanRelease = canEdit || can('loan:update');

  // Keyed under the Companies screen's key, as the header's company is, so a
  // change here refreshes every screen that reads the setting.
  const {
    data: company,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['settings-companies', companyId, 'money'],
    queryFn: () => api.settings.company(companyId!),
    enabled: !!companyId,
  });

  const updateSetting = useMutation({
    mutationFn: (body: Partial<CompanySettings>) =>
      api.settings.updateCompanySettings(companyId!, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['settings-companies'] });
      void queryClient.invalidateQueries({ queryKey: ['due-reminders'] });
    },
  });

  if (!companyId) {
    return (
      <>
        <PageHeader title="System Settings" />
        <Card className="p-5 text-sm text-slate-600">Pick a company in the header first.</Card>
      </>
    );
  }
  if (isLoading) return <Spinner />;
  if (error) return <ErrorState error={error} />;
  if (!company) return null;

  const settings = companySettingsOf(company);

  return (
    <>
      <PageHeader
        title="System Settings"
        subtitle={`Features switched on or off for ${company.name}. Each company in the tenant has its own.`}
      />
      <Card className="divide-y divide-slate-200">
        {COMPANY_SETTINGS_CATALOGUE.map((setting) => {
          const on = settings[setting.key];
          const inert = setting.indiaOnly && !money.india;
          const pending =
            updateSetting.isPending && updateSetting.variables?.[setting.key] !== undefined;
          return (
            <div key={setting.key} className="flex items-start justify-between gap-6 p-5">
              <div className="min-w-0">
                <p className="font-medium text-slate-900" id={`setting-${setting.key}`}>
                  {setting.label}
                </p>
                <p className="mt-1 text-sm text-slate-600">{setting.description}</p>
                {inert ? (
                  <p className="mt-1 text-xs text-amber-700">
                    Applies to companies registered in India only, so it has no effect for{' '}
                    {company.name}.
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={on}
                aria-labelledby={`setting-${setting.key}`}
                disabled={!canEdit || pending}
                title={canEdit ? undefined : 'Your role cannot change company settings'}
                onClick={() => updateSetting.mutate({ [setting.key]: !on })}
                className={`relative mt-1 inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/40 disabled:cursor-not-allowed disabled:opacity-60 ${
                  on ? 'bg-brand-700' : 'bg-slate-300'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                    on ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          );
        })}

        {/* Due-Date Reminder Scheduler */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 p-5">
          <div className="min-w-0 flex-1">
            <p
              className="font-medium text-slate-900"
              id={`setting-${DUE_DATE_REMINDER_SETTING.key}`}
            >
              {DUE_DATE_REMINDER_SETTING.label}
            </p>
            <p className="mt-1 text-sm text-slate-600">{DUE_DATE_REMINDER_SETTING.description}</p>
          </div>
          <div className="shrink-0 flex items-center gap-2">
            <select
              id="reminder-days-select"
              aria-label="Reminder threshold"
              value={settings.dueDateReminderDays ?? 0}
              disabled={!canEdit || updateSetting.isPending}
              onChange={(e) =>
                updateSetting.mutate({ dueDateReminderDays: Number(e.target.value) })
              }
              className="input w-48 text-sm"
              title={canEdit ? undefined : 'Your role cannot change company settings'}
            >
              {/* A value saved through the API need not be a preset; show it as it is. */}
              {DUE_DATE_REMINDER_OPTIONS.some(
                (opt) => opt.value === settings.dueDateReminderDays,
              ) ? null : (
                <option value={settings.dueDateReminderDays}>
                  {settings.dueDateReminderDays} days before
                </option>
              )}
              {DUE_DATE_REMINDER_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 p-5">
          <div className="min-w-0 flex-1">
            <p className="font-medium text-slate-900" id={`setting-${LOAN_RELEASE_SETTING.key}`}>
              {LOAN_RELEASE_SETTING.label}
            </p>
            <p className="mt-1 text-sm text-slate-600">{LOAN_RELEASE_SETTING.description}</p>
          </div>
          <div className="shrink-0">
            <select
              aria-labelledby={`setting-${LOAN_RELEASE_SETTING.key}`}
              value={settings.loanReleaseDays}
              disabled={!canEditLoanRelease || updateSetting.isPending}
              onChange={(e) => updateSetting.mutate({ loanReleaseDays: Number(e.target.value) })}
              className="input w-48 text-sm"
              title={canEditLoanRelease ? undefined : 'Your role cannot change company settings'}
            >
              {LOAN_RELEASE_SETTING.options.some(
                (opt) => opt.value === settings.loanReleaseDays,
              ) ? null : (
                <option value={settings.loanReleaseDays}>
                  {settings.loanReleaseDays} days before
                </option>
              )}
              {LOAN_RELEASE_SETTING.options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Card>
      {updateSetting.error ? (
        <p className="mt-3 text-sm text-red-700">{describeError(updateSetting.error)}</p>
      ) : null}
    </>
  );
}
